import { Bell, Building2, FolderTree, ListChecks, Mail, Sliders, ShieldCheck, Tags, Users, type LucideIcon } from 'lucide-react';

/**
 * Os cinco catálogos e seus endereços.
 *
 * A lista mora aqui, e não dentro da tela, porque o roteador também precisa
 * dela: cada aba tem que ter rota registrada, e uma aba sem rota é 404 na cara
 * de quem clicou. É o mesmo motivo de `app/nav.ts` existir separado do
 * `AppShell`.
 */

export const RAIZ_DA_ADMINISTRACAO = '/administracao';

export const CAMINHOS = {
  situacoes: 'situacoes',
  categorias: 'categorias',
  subcategorias: 'subcategorias',
  equipes: 'equipes',
  organizacoes: 'organizacoes',
  notificacoes: 'notificacoes',
  usuarios: 'usuarios',
  sla: 'sla',
  campos: 'campos',
  email: 'email',
} as const;

export type CaminhoDeCatalogo = (typeof CAMINHOS)[keyof typeof CAMINHOS];

export interface AbaDeCatalogo {
  caminho: CaminhoDeCatalogo;
  rotulo: string;
  icone: LucideIcon;
  /** O que a aba administra, em uma linha, para o cabeçalho da tela. */
  resumo: string;
}

export const ABAS: readonly AbaDeCatalogo[] = [
  {
    caminho: CAMINHOS.situacoes,
    rotulo: 'Situações',
    icone: ListChecks,
    resumo:
      'Os estados por que um chamado passa. A situação inicial é a que um chamado novo recebe — o tenant precisa de pelo menos uma ativa.',
  },
  {
    caminho: CAMINHOS.categorias,
    rotulo: 'Categorias',
    icone: FolderTree,
    resumo:
      'O primeiro dos dois níveis de classificação. A ordem daqui é a ordem do seletor na abertura de chamado.',
  },
  {
    caminho: CAMINHOS.subcategorias,
    rotulo: 'Subcategorias',
    icone: Tags,
    resumo:
      'O segundo e último nível. É ele que roteia o chamado e escolhe a política de SLA — e pertence sempre a uma categoria.',
  },
  {
    caminho: CAMINHOS.equipes,
    rotulo: 'Equipes',
    icone: Users,
    resumo: 'As filas de atendimento. Quem é membro de cada uma é assunto da tela de pessoas.',
  },
  {
    caminho: CAMINHOS.organizacoes,
    rotulo: 'Organizações',
    icone: Building2,
    resumo:
      'Os clientes atendidos. O domínio de e-mail associa o solicitante automaticamente quando o chamado chega pela caixa.',
  },
  {
    caminho: CAMINHOS.notificacoes,
    rotulo: 'Notificações',
    icone: Bell,
    resumo:
      'Controla quais e-mails o sistema envia e para quem. Sem configuração, todos estão habilitados.',
  },
  {
    caminho: CAMINHOS.usuarios,
    rotulo: 'Operadores',
    icone: Users,
    resumo: 'Os usuários internos que atendem chamados. O convite chega por e-mail após o cadastro.',
  },
  {
    caminho: CAMINHOS.sla,
    rotulo: 'Políticas de SLA',
    icone: ShieldCheck,
    resumo:
      'As metas de tempo de atendimento. Cada política vale para uma métrica (primeira resposta, resolução, etc.) e um tipo de compromisso (SLA, OLA, UC).',
  },
  {
    caminho: CAMINHOS.campos,
    rotulo: 'Campos de chamado',
    icone: Sliders,
    resumo:
      'Os rótulos de prioridade, urgência, impacto e tipo de chamado. O valor do enum permanece como âncora; o que muda é o que o operador lê na tela.',
  },
  {
    caminho: CAMINHOS.email,
    rotulo: 'E-mail',
    icone: Mail,
    resumo:
      'Equipe, prioridade e categoria padrão para chamados abertos automaticamente pela caixa de entrada.',
  },
] as const;

export const abaDe = (caminho: string): AbaDeCatalogo | undefined =>
  ABAS.find((aba) => aba.caminho === caminho);
